// Where a pop-up or a line of Board activity goes when clicked (Features 1, 20 Oct): the board it happened on, and
// the page, step, post, deal, idea, task or widget it was about, opened as clicking it there does. Something deleted
// since opens its board and says it is gone.

import { kindOf } from "../../shared/boardKinds";
import { openTab } from "../boards/sheet/state";
import { readTasks } from "../../shared/catalog";
import { widgetIds } from "../../shared/docblocks";
import type { ActivityTo } from "../../shared/types";
import { openPost } from "../boards/calendar/state";
import { openPage, showChange } from "../boards/doc/state";
import { selectIdea } from "../boards/mindmap/state";
import { openDeal } from "../boards/pipeline/state";
import { openSlide } from "../boards/slides/state";
import { setAt as showOnCalendar } from "../boards/agenda/state";
import { camera } from "../canvas/camera";
import { itemRect } from "../canvas/geometry";
import { exitFullscreen } from "../chrome/Fullscreen";
import { switchBoard } from "./actions";
import { get, glowItems, set, toast } from "./store";

/** What a thing on a board of that kind is called, for "That post is gone". */
function noun(kind: string, to: ActivityTo): string {
  if (to.task) return "task";
  if (kind === "agenda") return "event";
  if (kind === "calendar") return "post";
  if (kind === "pipeline") return "deal";
  if (kind === "mindmap") return "idea";
  if (kind === "plan") return "step";
  if (kind === "doc") return "page";
  if (kind === "slides") return "slide";
  return to.item ? "widget" : "thing";
}

/** After the board has drawn: an element brought into the middle of the screen when it is off it. */
function bringIntoView(selector: string, canvas: boolean) {
  setTimeout(() => {
    const el = document.querySelector<HTMLElement>(selector);
    if (!el) return;
    if (!canvas) {
      el.scrollIntoView({ block: "center", behavior: "smooth" });
      return;
    }
    const r = el.getBoundingClientRect();
    const off = r.right < 0 || r.bottom < 0 || r.left > window.innerWidth || r.top > window.innerHeight;
    if (off) camera.panBy(window.innerWidth / 2 - (r.left + r.width / 2), window.innerHeight / 2 - (r.top + r.height / 2));
  }, 260);
}

/** A task in a to-do list, lit for a moment so the eye finds it. */
function flashTask(id: string) {
  setTimeout(() => {
    const el = document.querySelector<HTMLElement>(`[data-task-id="${CSS.escape(id)}"]`);
    if (!el) return;
    el.scrollIntoView({ block: "nearest", behavior: "smooth" });
    el.classList.remove("is-flash");
    void el.offsetWidth;
    el.classList.add("is-flash");
    setTimeout(() => el.classList.remove("is-flash"), 1800);
  }, 320);
}

/**
 * Opens what a line or a pop-up is about, as a click on it there would. glow (the chat's buttons to where an agent made
 * a change, Features 3): what it opens glows once too, and a page shows the newest writing in it.
 */
export function goTo(to: ActivityTo, opts: { glow?: boolean } = {}): void {
  const s = get();
  // an archived board opens read only, as from Archived
  const board = s.boards.find((b) => b.id === to.board) ?? s.archived.find((b) => b.id === to.board);
  if (!board) {
    toast({ level: "info", title: "That board is gone", body: "It was deleted since. It may be in the trash for a while." });
    return;
  }
  if (s.presenting && board.id !== s.boardId) exitFullscreen({ keep: true });
  // All boards closes for the board it opens, as a click on one does
  if (s.panel === "boards") set({ panel: null, panelArg: null });
  if (board.id !== s.boardId) switchBoard(board.id);
  const kind = kindOf(board);
  const gone = (what: string) => toast({ level: "info", title: `That ${what} is gone`, body: `It was deleted since, so here is ${board.name}.` });

  if (to.entry) {
    const e = get().entries[to.entry] ?? null;
    if (!e || e.boardId !== board.id) {
      gone(noun(kind, to));
      return;
    }
    if (kind === "doc" || kind === "plan") {
      // a question on a plan is about its step, which shows it
      const page = e.t === "question" && e.parent ? (get().entries[e.parent] ?? null) : e;
      if (page) openPage(board.id, page.id);
      if (page && opts.glow) showChange(page.id);
    } else if (kind === "calendar") {
      openPost(board.id, e.id);
      bringIntoView(`[data-post="${CSS.escape(e.id)}"]`, false);
    } else if (kind === "pipeline") {
      openDeal(e.id);
      bringIntoView(`[data-deal="${CSS.escape(e.id)}"]`, false);
    } else if (kind === "mindmap") {
      selectIdea(board.id, e.id);
      // a phone shows the map as an outline, which scrolls; a computer's map moves under the camera
      bringIntoView(`[data-idea="${CSS.escape(e.id)}"]`, !get().mobile);
    } else if (kind === "slides") {
      // a deck opens on the slide
      if (e.t === "slide") openSlide(board.id, e.id);
    } else if (kind === "agenda") {
      // a Calendar board goes to the event's day
      if (e.t === "event" && typeof e.data.start === "string") showOnCalendar(board.id, e.data.start.slice(0, 10));
    } else if (kind === "sheet") {
      // a sheet's tab, or the tab a band of its cells is in
      const tab = e.t === "cells" ? e.parent : e.id;
      if (tab) openTab(board.id, tab);
    }
  }

  if (to.entry && opts.glow && get().entries[to.entry]) glowItems([to.entry]);

  if (to.item) {
    const item = get().items[to.item];
    if (!item || item.boardId !== board.id) {
      // a widget in a page that went: the page opened above already says where it was
      if (!to.entry) gone(to.task ? "list" : "widget");
      return;
    }
    // a widget on a slide: that slide opens with it
    if (!to.entry && kind === "slides") {
      const holder = Object.values(get().entries).find((x) => x.boardId === board.id && x.t === "slide" && x.data.widget === item.id);
      if (holder) openSlide(board.id, holder.id);
    }
    // a widget in a page of a doc or plan board: that page opens with it
    if (!to.entry && (kind === "doc" || kind === "plan")) {
      const holder = Object.values(get().entries).find((x) => x.boardId === board.id && x.t === "page" && widgetIds(x.data.body).includes(item.id));
      if (holder) openPage(board.id, holder.id);
    }
    set({ selection: [item.id] });
    glowItems([item.id]);
    // a phone shows a board as a column, whose tiles scroll into view; a computer's canvas flies to it. A page just
    // opened draws its widgets a moment later, so its tile is looked for again for a second or two
    const find = () => document.querySelector(get().mobile ? `[data-widget="${CSS.escape(item.id)}"], [data-item-id="${CSS.escape(item.id)}"]` : `[data-item-id="${CSS.escape(item.id)}"]`);
    const bring = (tries: number) => {
      if (!get().mobile && kind === "canvas") return camera.fit(itemRect(item), { maxZ: 1, pad: 140 });
      const el = find();
      if (el) el.scrollIntoView({ block: "center", behavior: matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
      else if (tries < 8) setTimeout(() => bring(tries + 1), 250);
    };
    setTimeout(() => bring(0), 250);
    if (to.task) {
      if (item.kind === "widget" && readTasks(item.props.tasks).some((t) => t.id === to.task)) flashTask(to.task);
      else toast({ level: "info", title: "That task is gone", body: "It was deleted since, so here is its list." });
    }
  }
}
